import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from './config/firebase';

const samplePosts = [
  {
    title: "No one of us is as smart as ALL of us",
    content: `In 2006, 60-70% of Netflix's movie rentals came from recommendations made to customers by a system called "Cinematch."

Netflix wanted to elevate its game, so it launched a global challenge: Improve our recommendation engine by 10% and earn $1 million.

Brilliant individuals and small academic teams were soon to learn the in-the-face message in Ringo Starr's first solo hit, "It Don't Come Easy."

The competition lasted almost 3 years.

No individual or single team was able to achieve the goal.

It wasn't until several top teams merged—pooling models, approaches, and code—that the breakthrough finally occurred.

As egos faded, individuals and teams began working together.
Competition gave way to collaboration.
Collaborating teams transformed into 'super teams.'

The prize was awarded on September 21, 2009.

The outcome of the competition had an enormous impact on the field, directly influencing modern recommender systems, including those used by YouTube and Spotify.

This remains one of the clearest real-world demonstrations that, under the right conditions, collective intelligence can, and most often does, dramatically outperform even the best individual experts or closed corporate teams.

**"No one of us is as smart as all of us" is not just a slogan.**

**It's a strategy.**

Innovation accelerates when we stop competing for credit and start combining strengths.

Where might cooperation generate a breakthrough in your world today?`,
    featuredImage: "",
    published: true,
    createdAt: Timestamp.fromDate(new Date()),
    updatedAt: Timestamp.fromDate(new Date()),
  },
  {
    title: "The red zone, wasted",
    content: `Sports cars have a tachometer, a gauge showing how close the motor is to melting down.

When the revs enter the red zone, performance is enhanced–for a while. Do it too much or for too long, and you'll burn out.

In our work, there are two sorts of red zones.

Athletes know that the last 5% of their effort accounts for 100% of their success. The same is true for pianists and any endeavor where we are sorted by elite performance.

But many people work in industries where the last 5% of their effort might account for just 5% of their success. A stockbroker who stays busy cold calling clients late into the night doesn't receive increasing rewards–they just get a little busier.

The problem with the linear reward curve is obvious–people who are driven to get just a little more repeatedly do just a little more, until they've broken everything they said they wanted.

If you're going into the red zone, be clear about what the outcome is supposed to be.`,
    featuredImage: "",
    published: true,
    createdAt: Timestamp.fromDate(new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)),
    updatedAt: Timestamp.fromDate(new Date(Date.now() - 1 * 24 * 60 * 60 * 1000)),
  },
  {
    title: "The smallest viable audience",
    content: `It's tempting to want everyone to like your work.

But "everyone" is not a useful target market. The magic happens when you pick the smallest group that can sustain you.

**Who are you trying to reach?**

Not demographics. Real people with real problems who are waiting for someone to show up and say, "I made this for you."

When you focus on the smallest viable audience, you can stop trying to please everyone. You can make something that matters to *someone*.

The Beatles didn't try to please everyone. They tried to please teenage girls in Liverpool. And then the world followed.

Find your smallest viable audience. Delight them. The rest will take care of itself.`,
    featuredImage: "",
    published: true,
    createdAt: Timestamp.fromDate(new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)),
    updatedAt: Timestamp.fromDate(new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)),
  },
  {
    title: "Shipping creative work",
    content: `The resistance never goes away.

Every time you sit down to create something that matters, the resistance will be there. Telling you it's not good enough. Telling you to wait. Telling you to polish it just a little more.

But here's the thing: **the work isn't finished until you ship it.**

A blog post sitting in drafts isn't a blog post. A song that never leaves your bedroom isn't a song. An idea that stays in your head isn't an idea—it's a dream.

Shipping is the act of turning dreams into reality.

It's scary because once you ship, you can be judged. But the alternative is worse: never knowing what could have been.

Ship today. Ship imperfect. Ship scared.

Just ship.`,
    featuredImage: "",
    published: true,
    createdAt: Timestamp.fromDate(new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)),
    updatedAt: Timestamp.fromDate(new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)),
  },
  {
    title: "The practice",
    content: `Professionals don't wait for inspiration.

They show up every day and do the work. Not because they feel like it, but because that's what professionals do.

The amateur waits until they're in the mood. The professional creates a mood by working.

**Inspiration is for amateurs. The rest of us just show up and get to work.**

This doesn't mean the work is joyless. Far from it. The joy comes from the practice itself, from the showing up, from the slow accumulation of craft.

You don't become a writer by having written. You become a writer by writing. Every day. Especially when you don't feel like it.

What's your practice?`,
    featuredImage: "",
    published: true,
    createdAt: Timestamp.fromDate(new Date(Date.now() - 5 * 24 * 60 * 60 * 1000)),
    updatedAt: Timestamp.fromDate(new Date(Date.now() - 5 * 24 * 60 * 60 * 1000)),
  },
  {
    title: "Permission to fail",
    content: `What would you attempt if you knew you could not fail?

It's a useful question, but it's the wrong one.

The right question is: **What's worth doing even if you fail?**

Because you will fail. Not might. Will. The question is whether the journey is worth it anyway.

Learning to ride a bike involves falling. Learning to write involves writing badly. Learning to lead involves making mistakes that affect real people.

The cost of failure is rarely as high as we imagine. The cost of not trying is almost always higher than we think.

Give yourself permission to fail. It's the only way to give yourself permission to succeed.`,
    featuredImage: "",
    published: true,
    createdAt: Timestamp.fromDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)),
    updatedAt: Timestamp.fromDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)),
  },
];

export async function seedPosts() {
  console.log('Seeding posts...');

  for (const post of samplePosts) {
    try {
      const docRef = await addDoc(collection(db, 'posts'), post);
      console.log('Added post:', docRef.id, post.title);
    } catch (error) {
      console.error('Error adding post:', error);
    }
  }

  console.log('Done seeding posts!');
}
